import React, { useCallback, useEffect, useRef } from "react";
import classNames from "classnames";
import { Combobox, ComboboxButton, ComboboxInput, ComboboxInputProps, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import Button from "@/component/Button";
import InViewObserver from "@/component/InViewObserver";
import { CloseIcon } from "@/assets/svgs/icons";


interface BaseTypeaheadProps {
    invalid?: boolean;
    invalidText?: string;
    children?: React.ReactNode;
}

interface SingleTypeaheadProps extends BaseTypeaheadProps {
    multiple?: false;
    selection?: string;
    onChange: (selected: string) => void;
}

interface MultipleTypeaheadProps extends BaseTypeaheadProps {
    multiple: true;
    selection: string[];
    onChange: (selected: string[]) => void;
}

type TypeaheadProps = SingleTypeaheadProps | MultipleTypeaheadProps;

export const Typeahead: React.FC<TypeaheadProps> = (props) => {
    const {
        multiple,
        selection,
        onChange,
        children,
    } = props;

    const onSelect = (selected: any) => {
        if (!selected) {
            return;
        }
        onChange(selected);
    };

    return (
        <Combobox as="div"
                  multiple={multiple}
                  className="relative w-full"
                  value={selection}
                  onChange={onSelect}
        >
            {multiple && (
                <div className="mb-1 flex flex-wrap gap-0.5">
                    {selection.map(item => (
                        <div key={item}
                             className="px-2 py-1 inline-flex items-center bg-chat42-10 rounded-[12px] text-[14px] text-chat42"
                        >
                            {item}
                            <Button className="ml-2 text-chat42"
                                    onClick={() => onChange(selection.filter(s => s !== item))}
                            >
                                <CloseIcon className="size-3.5" />
                            </Button>
                        </div>
                    ))}
                </div>
            )}
            {children}
        </Combobox>
    );
};

export const TypeaheadInput: React.FC<ComboboxInputProps & {invalid?: boolean, invalidText?: string, selection?: string | string[]}> = (props) => {
    const { invalid, invalidText, selection, onBlur: propsOnBlur, ...rest } = props;

    const selectionValue = (!Array.isArray(selection) && selection) || "";

    const ref = useRef<HTMLInputElement>(null);
    const isComposingRef = useRef(false);

    const onBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        ref.current!.value = selectionValue;
        propsOnBlur?.(e);
    }, [selectionValue, propsOnBlur]);

    useEffect(() => {
        ref.current!.value = selectionValue;
    }, [selectionValue]);

    return (<>
        <InViewObserver enabled={!!(ref.current && document.activeElement === ref.current)}
                        onHidden={() => ref.current?.blur()}
        >{inViewRef => (
            <ComboboxButton as="div" ref={inViewRef}>
                <ComboboxInput ref={ref}
                               autoComplete="off"
                               className={classNames(
                                   "dropdown-button placeholder",
                                   invalid && "input-invalid"
                               )}
                               onKeyDown={e => {
                                   if (e.key === " ") {
                                       e.stopPropagation();
                                   }
                                   props.onKeyDown?.(e);
                               }}
                               onBlur={onBlur}
                               {...rest}
                               onCompositionStart={() => isComposingRef.current = true}
                               onCompositionEnd={() => isComposingRef.current = false}
                />
            </ComboboxButton>
        )}</InViewObserver>
        {invalidText && invalid && !ref.current?.value && (
            <span className="mt-1 font-[400] text-[14px] leading-none text-point-red">
                {invalidText}
            </span>
        )}
    </>);
};

export const TypeaheadOptions: React.FC<{options: string[]}> = ({ options }) => {
    return (
        <ComboboxOptions anchor={{ to: "bottom", gap: 4 }}
                         className="w-[var(--input-width)] !max-h-60 bg-white border border-black-300 rounded-[6px] overflow-auto z-10 empty:border-none"
        >
            {options.map(option => (
                <ComboboxOption key={option}
                                value={option}
                                className="px-2 py-1 text-[14px] text-black-500 cursor-pointer data-[active]:bg-chat42-50 data-[active]:text-white"
                >
                    {option}
                </ComboboxOption>
            ))}
        </ComboboxOptions>
    );
};
